import React,{ createContext,useCallback,useContext,useEffect,useMemo,useRef,useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SESSION_UPDATED_EVENT } from '../brandKits';
import { useCmaInfoReportController } from '../components/cma/CmaInfoReportPanel';
import { propertyReportSnapshot } from '../utils/propertyReportSnapshot';

const Context=createContext(null);
const idle={ availability:null,matches:[],busy:false,message:'Checking CMA Info availability…',gaps:[],status:'idle',result:null,resultQuery:null };
function sessionActor() {
    try {
        const stored=JSON.parse(localStorage.getItem('user') || 'null');
        const user=stored?.user || stored;
        return String(user?._id || user?.id || '');
    } catch { return ''; }
}
const caseKey=({ userId,leadId,propertyId,address }) => JSON.stringify([
    String(userId || ''),String(leadId || ''),String(propertyId || ''),String(address || ''),
]);
function sourceIdentity(snapshot) {
    const provider=snapshot?.source?.provider || (snapshot?.savedFile?.id ? 'lightstone' : '');
    // Field edits matter even when a saved report retains its provider/receipt.
    // The report page adds these two local bookkeeping values on every apply.
    const stable=value => Array.isArray(value) ? value.map(stable)
        : value && typeof value==='object' ? Object.keys(value).sort().reduce((out,key) => {
            if (key!=='savedAt' && key!=='reportContext') out[key]=stable(value[key]);
            return out;
        },{}) : value;
    return { provider,key:JSON.stringify(stable(snapshot || {})) };
}

function Job({ job,publish }) {
    const controller=useCmaInfoReportController({ ...job.input,query:job.query,scopeKey:job.key });
    useEffect(() => { publish(job.key,job.epoch,job.revision,controller); },[job.key,job.epoch,job.revision,controller,publish]);
    return null;
}

// Session memory only: route changes retain requests; refresh/logout cannot
// restore an intent or automatically repeat a provider lookup.
export function CmaInfoRetrievalProvider({ children,actorId }) {
    const navigate=useNavigate();
    const [jobs,setJobs]=useState({});
    const [epoch,setEpoch]=useState(0);
    const identity=useRef({ actorId:String(actorId ?? sessionActor()),epoch:0 });
    const isCurrent=useCallback((actor,version) => String(actor || '')===identity.current.actorId
        && version===identity.current.epoch
        && String(actorId ?? sessionActor())===identity.current.actorId,[actorId]);
    useEffect(() => {
        const reset=() => {
            const next=String(actorId ?? sessionActor());
            identity.current={ actorId:next,epoch:identity.current.epoch+1 };
            setJobs({});setEpoch(identity.current.epoch);
        };
        if (String(actorId ?? sessionActor())!==identity.current.actorId) reset();
        const changed=() => {
            if (String(actorId ?? sessionActor())!==identity.current.actorId) reset();
        };
        window.addEventListener(SESSION_UPDATED_EVENT,changed);
        window.addEventListener('storage',changed);
        return () => {
            window.removeEventListener(SESSION_UPDATED_EVENT,changed);
            window.removeEventListener('storage',changed);
        };
    },[actorId]);
    const attach=useCallback((input,consumer) => {
        const version=identity.current.epoch;
        if (!input.userId || !input.leadId || !isCurrent(input.userId,version)) return;
        const key=caseKey(input);
        setJobs(previous => {
            const old=previous[key];
            if (old?.consumers.includes(consumer)) return previous;
            return { ...previous,[key]:old
                ? { ...old,consumers:[...old.consumers,consumer] }
                : { key,input,query:input.query || input.address || '',initialSourceKey:input.initialSourceKey,
                    epoch:version,revision:0,consumers:[consumer],controller:null } };
        });
    },[isCurrent]);
    const detach=useCallback((key,consumer) => setJobs(previous => {
        const old=previous[key];
        if (!old?.consumers.includes(consumer)) return previous;
        return { ...previous,[key]:{ ...old,consumers:old.consumers.filter(value => value!==consumer) } };
    }),[]);
    const publish=useCallback((key,version,revision,controller) => setJobs(previous => {
        const old=previous[key];
        if (!old || old.revision!==revision || !isCurrent(old.input.userId,version) || old.controller===controller) return previous;
        return { ...previous,[key]:{ ...old,controller } };
    }),[isCurrent]);
    const replaceSource=useCallback((key,initialSourceKey) => setJobs(previous => {
        const old=previous[key];
        if (!old || !isCurrent(old.input.userId,old.epoch)) return previous;
        return { ...previous,[key]:{ ...old,initialSourceKey,revision:old.revision+1,controller:null } };
    }),[isCurrent]);
    const setQuery=useCallback((key,query) => setJobs(previous => {
        const old=previous[key];
        if (!old || !isCurrent(old.input.userId,old.epoch) || old.query===query) return previous;
        return { ...previous,[key]:{ ...old,query } };
    }),[isCurrent]);
    const value=useMemo(() => ({ jobs,epoch,attach,detach,setQuery,isCurrent,replaceSource }),[jobs,epoch,attach,detach,setQuery,isCurrent,replaceSource]);
    const away=Object.values(jobs).filter(job => !job.consumers.length && job.controller?.status!=='idle'
        && job.controller && isCurrent(job.input.userId,job.epoch));
    return <Context.Provider value={value}>
        {Object.values(jobs).map(job => <Job key={`${job.epoch}:${job.revision}:${job.key}`} job={job} publish={publish} />)}
        {children}
        {away.length>0 && <aside aria-label="CMA Info retrievals" style={{ position:'fixed',bottom:20,left:20,zIndex:2147483000,display:'grid',gap:8 }}>
            {away.map(job => <button key={job.key} type="button" onClick={() => {
                if (!isCurrent(job.input.userId,job.epoch)) return;
                const lead=job.input.reopenLead;
                if (String(lead?.id || lead?._id || '')===String(job.input.leadId)) {
                    navigate(`/crm/cma-report?leadId=${encodeURIComponent(job.input.leadId)}`,{ state:{ lead } });
                } else {
                    // Never invent a lead or reopen whichever other client was
                    // last mirrored in the browser. The normal client page can
                    // load this exact authorized record before opening its CMA.
                    navigate(`/clients/${encodeURIComponent(job.input.leadId)}`);
                }
            }} style={{ background:'#fff',border:'1px solid #11575C',borderRadius:10,padding:12,maxWidth:340,textAlign:'left' }}>
                <strong>CMA Info · {job.controller.busy ? 'Retrieving property data' : job.controller.status==='done' ? 'Report ready' : 'Open to review'}</strong>
                <span style={{ display:'block' }}>{job.query}</span>
                <span>Return to report</span>
            </button>)}
        </aside>}
    </Context.Provider>;
}

export function useCmaInfoRetrieval(input) {
    const store=useContext(Context);
    const key=caseKey(input);
    const consumer=useRef(Symbol('cma-report'));
    const callbacks=useRef(input);
    callbacks.current=input;
    const applied=useRef(null);
    const attach=store?.attach,detach=store?.detach;
    const job=store?.jobs[key];
    const currentSource=sourceIdentity(input.initialValues);
    const retainedSource=job?.controller?.result ? sourceIdentity(propertyReportSnapshot(job.controller.result,input)).key : null;
    const replaced=!!job && !!currentSource.provider && currentSource.key!==job.initialSourceKey && currentSource.key!==retainedSource;
    useEffect(() => {
        if (!attach || input.enabled===false) return undefined;
        attach({ userId:input.userId,leadId:input.leadId,propertyId:input.propertyId,address:input.address,
            query:input.query,reopenLead:input.reopenLead,initialSourceKey:currentSource.key },consumer.current);
        const id=consumer.current;
        return () => detach(key,id);
    // The key contains every case primitive; callbacks are kept current separately.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[attach,detach,key,input.enabled,store?.epoch]);
    useEffect(() => {
        if (replaced) store.replaceSource(key,currentSource.key);
    },[replaced,key,currentSource.key,store?.replaceSource]); // eslint-disable-line react-hooks/exhaustive-deps
    const controller=replaced ? null : job?.controller;
    useEffect(() => {
        if (!store) return;
        callbacks.current.onAvailability?.(controller?.availability?.available===true);
        callbacks.current.onBusyChange?.(controller?.busy===true);
    },[!!store,controller?.availability,controller?.busy,key]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!job || !controller?.result || controller.resultQuery!==job.query || !store.isCurrent(input.userId,job.epoch)
            || input.enabled===false || (applied.current?.key===key && applied.current?.result===controller.result)) return;
        applied.current={ key,result:controller.result };
        callbacks.current.onResult?.(controller.result);
    },[key,job,controller,input.enabled,input.userId,store]);
    if (!store) return null;
    return { ...idle,...controller,query:job?.query ?? input.query ?? '',
        setQuery:query => store.setQuery(key,query),
        replaceSource:snapshot => store.replaceSource(key,sourceIdentity(snapshot).key),
        request:(...args) => {
            if (job && store.isCurrent(input.userId,job.epoch)) return controller?.request(...args);
        },
        refresh:() => { if (job && store.isCurrent(input.userId,job.epoch)) controller?.refresh(); } };
}
